// Core imports
import { Fragment, useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import moment from 'moment';

// Component imports
import { Button, Checkbox, Col, Form, Popconfirm, Row, Space, Spin, Table, Tooltip, Typography, Progress } from 'antd';
import { EyeOutlined, StopOutlined, LoadingOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import KnowledgeBaseArticlesModal from '../Modals/KnowledgeBaseArticlesModal';
import DeleteConfirmationModal from '../Modals/DeleteConfirmationModal';
import Permissioned from '../Permissioned';
import { useCancelJobMutation, useFetchJobsQuery } from '../../store/jobs/jobsApi';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { DEFAULT_DATE_FORMAT, JOB_STATUSES, PERMISSIONS_GROUP_EDITOR, PERMISSIONS_GROUP_VIEWER, KNOWLEDGE_BASE_TYPES, KNOWLEDGE_BASE_SPACES } from '../../constants/main';
import { IS_HRWIZ_SHARED_KNOWLEDGE_BASES_HIDDEN } from '../../constants/env';
import { useDeleteKnowledgeBaseMutation, useFetchKnowledgeBasesQuery, useUpdateKnowledgeBaseMutation } from '../../store/knowledgeBases/knowledgeBasesApi';
import useUploadKnowledgeBaseFile from '../../utils/hooks/useUploadKnowledgeBaseFile';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import { KNOWLEDGE_BASE_UPLOAD_STATUSES } from '../../constants/knowledgeBases';

// Constant declarations
const PAGE_SIZE = 1000;

const HRWizKnowledgeBaseSourcesTable = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const { tenantId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userPermissions = useUserPermissions(orgId, tenantId);

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [isConnectKBModalOpen, setIsConnectKBModalOpen] = useState(false);
  const [pollKnowledgeBase] = useState(false);
  const [isPollingUploads, setIsPollingUploads] = useState(true);
  const [knowledgeBaseToEdit, setKnowledgeBaseToEdit] = useState();
  const [knowledgeBases, setKnowledgeBases] = useState({ items: [] });
  const [isCancelUploadModalOpen, setIsCancelUploadModalOpen] = useState(false);
  const [runningUploadId, setRunningUploadId] = useState(null);

  // Redux state
  const { data: _knowledgeBases, isFetching: isFetchingKnowledgeBases, refetch: refetchKnowledgeBases } = useFetchKnowledgeBasesQuery({
    orgId,
    urlParams: {
      includeTenants: true,
      includeIntegration: true,
      space: KNOWLEDGE_BASE_SPACES.EX,
      ...(IS_HRWIZ_SHARED_KNOWLEDGE_BASES_HIDDEN && { type: [KNOWLEDGE_BASE_TYPES.INTEGRATION, KNOWLEDGE_BASE_TYPES.INTERNAL] }),
      page: currentPage -1,
      pageSize: PAGE_SIZE,
      sortBy: searchParams.get('sortBy') || 'createdAt',
      order: searchParams.get('order') || 'desc',
      ...(searchParams.get('searchTerm') && { name: searchParams.get('searchTerm') })
    }
  },
  {
    pollingInterval: pollKnowledgeBase ? 3000 : 0
  });
  const { data: runningUploads } = useFetchJobsQuery({ orgId, urlParams: { status: [JOB_STATUSES.STARTING, JOB_STATUSES.RUNNING] }, page: 0, pageSize: 1000 }, { pollingInterval: 2000, skip: !orgId || !isPollingUploads });
  const [ cancelUpload, { isLoading: isCancelingUpload }] = useCancelJobMutation();
  const isFetchingKnowledgeBasesWithoutPolling = isFetchingKnowledgeBases && !pollKnowledgeBase;
  const [updateKnowledgeBase, { isLoading: isUpdatingKnowledgeBase }] = useUpdateKnowledgeBaseMutation();
  const [deleteKnowledgeBase] = useDeleteKnowledgeBaseMutation();

  const onKnowledgeBaseFileUploaded = useCallback(() => {
    setTimeout(refetchKnowledgeBases, 100);
    setIsPollingUploads(true);
  }, [refetchKnowledgeBases]);

  const { status: uploadStatus, createdKnowledgeBaseId, renderUploadFileButton, showModalForFileUpload } = useUploadKnowledgeBaseFile({
    orgId,
    autoCreateKnowledgeBase: true,
    newKnowledgeBaseData: tenantId ? {
      tenantAssociationRefs: [{
        tenantId,
        useGenerative: true
      }]
    } : {},
    showSuccessMessages: false,
    onFileUploaded: onKnowledgeBaseFileUploaded
  });
  // const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);
  // const hasEditAccess = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, false);

  useEffect(() => {
    if (runningUploads?.items?.filter((upload) => upload.status === JOB_STATUSES.RUNNING || upload.status === JOB_STATUSES.STARTING).length === 0 && isPollingUploads) {
      setIsPollingUploads(false);
    }
  }, [runningUploads]);

  useEffect(() => {
    const items = [];
    const activeKBs = [];
    _knowledgeBases?.items?.forEach(tmp => {
      if (tmp.spaces?.includes('ex')) {
        items.push(tmp);
        if (tmp.tenantAssociations?.find((ta) => ta.tenantId === tenantId) != null) {
          activeKBs.push(tmp);
        }
      }
    });
    setKnowledgeBases({ items });

    // assume the oldest KB is the default KB.. this is a temporary solution until we discuss how to handle default KBs
    items.sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));

  }, [_knowledgeBases, tenantId, orgId]);

  const updateUrlSearchParams = (current = 1, sorting = { createdAt: 'desc' }, filters) => {
    setCurrentPage(current);
    const urlSearchParams = new URLSearchParams(location.search);

    const searchTerm = form.getFieldValue('searchTerm');
    urlSearchParams.set('page', current - 1);
    urlSearchParams.set('pageSize', PAGE_SIZE);

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    if (searchTerm) {
      urlSearchParams.set('searchTerm', encodeURIComponent(searchTerm));
    } else {
      urlSearchParams.set('searchTerm', encodeURIComponent(''));
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const onKnowledgeBaseDisconnectAndDelete = async (record) => {
    const isDeleted = await deleteKnowledgeBase({ orgId: orgId, id: record.id }).unwrap();
    if (isDeleted) {
      refetchKnowledgeBases();
    }
  };

  const onConnectKBModalClose = () => {
    setIsConnectKBModalOpen(false);
    setKnowledgeBaseToEdit(null);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'createdAt' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, sorting, filters);
  };

  const renderCancelUploadModalContent = () => {
    return (
      <Row gutter={24} className="mt-3">
        <Col span={24} className ="d-flex justify-content-center">
          <Typography.Text className="fs-lg">
            {t('This action will stop the upload progress.')}
          </Typography.Text>
        </Col>
      </Row>
    );
  };

  const openCancelUploadModal = (jobId) => {
    setRunningUploadId(jobId);
    setIsCancelUploadModalOpen(true);
  };

  const closeCancelUploadModal = () => {
    setRunningUploadId(null);
    setIsCancelUploadModalOpen(false);
  };

  const onCancelUpload = async () => {
    await cancelUpload({ orgId: orgId, id: runningUploadId });
    closeCancelUploadModal();
  };

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'createdAt';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  const columns = [
    {
      title: t('Name'),
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortingInfo.columnKey === 'name' && sortingInfo.order,
      render: record => {
        return record ? (
          <Space>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchParams.get('searchTerm')]}
              autoEscape
              textToHighlight={record.name}
            />
          </Space>
        ) : null;
      }
    },
    {
      title: t('Date'),
      key: 'createdAt',
      sorter: (a, b) => a.createdAt > b.createdAt,
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      render: record => {
        return record ? (
          <Space>
            <p>{moment(record.createdAt).format(DEFAULT_DATE_FORMAT)}</p>
          </Space>
        ) : null;
      }
    },
    {
      title: t('Active'),
      key: 'Active',
      fixed: 'right',
      align: 'center',
      width: 100,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        const connected = record?.tenantAssociations?.find(tmp => tmp.tenantId === tenantId) != null;
        const tooltip = connected ? t('Deactivate') : t('Activate');
        return (
          <Tooltip title={tooltip}>
            <Checkbox
              defaultChecked={connected}
              disabled={isUpdatingKnowledgeBase}
              onChange={async (e) => {
                let tenantAssociationRefs = [];
                if (e.target.checked) {
                  tenantAssociationRefs = [
                    ...record?.tenantAssociations || [],
                    {
                      tenantId,
                      useGenerative: true
                    }
                  ];
                } else {
                  tenantAssociationRefs = record.tenantAssociations.filter(tmp => tmp.tenantId !== tenantId);
                }
                const dataToSubmit = {
                  tenantAssociationRefs
                };

                await updateKnowledgeBase({ orgId: orgId, id: record.id, data: dataToSubmit });
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 200,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        const items = Array.isArray(runningUploads) ? runningUploads : Array.isArray(runningUploads?.items) ? runningUploads?.items : [];
        const job = items.find((job) => job.objectId === record.id);
        if (job) {
          return (
            <Row justify={'space-between'} align={'middle'}>
              <Col className="d-flex" span={8}>
                <Progress percent={job.progress} size="large" type="circle" status="active" />
              </Col>
              <Col span={16}>
                <Button
                  size="large"
                  className="px-5"
                  danger
                  block
                  onClick={() => openCancelUploadModal(job.id)}
                  loading={isCancelingUpload}
                  icon={<StopOutlined />}
                >
                  {t('Cancel')}
                </Button>
              </Col>
            </Row>
          );
        }
        if (uploadStatus === KNOWLEDGE_BASE_UPLOAD_STATUSES.UPLOADING && createdKnowledgeBaseId === record.id) {
          return (
            <Space>
              <Spin indicator={<LoadingOutlined spin />} />
            </Space>
          );
        }
        return (
          <Space>
            <Tooltip title={t('Preview articles')}>
              <Button
                shape="circle"
                type="text"
                icon={<EyeOutlined />}
                loading={isFetchingKnowledgeBasesWithoutPolling || isUpdatingKnowledgeBase}
                onClick={() => {
                  setKnowledgeBaseToEdit(record);
                  setIsConnectKBModalOpen(true);
                }}
              />
            </Tooltip>

            <Popconfirm
              title={t('Delete from Agent\'s KB sources list?')}
              okText={t('Yes')}
              cancelText={t('No')}
              placement="topRight"
              onConfirm={() => {
                onKnowledgeBaseDisconnectAndDelete(record);
              }}
            >
              <Tooltip title={'Delete articles'}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<DeleteIcon style={{ color: 'red' }} />}
                  loading={isFetchingKnowledgeBasesWithoutPolling || isUpdatingKnowledgeBase}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <Fragment>
      <Row justify="space-between" align="middle" className="mb-5">
        <Col>
        </Col>
        <Col flex={'auto'} className="d-flex justify-content-end" span={24}>
          <Space>
            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >
              {
                renderUploadFileButton(
                  <Button
                    icon={<PlusIcon />}
                    type="primary"
                    className="border-0 float-right"
                    size="large"
                    onClick={showModalForFileUpload}
                    loading={isFetchingKnowledgeBasesWithoutPolling || isUpdatingKnowledgeBase}
                  >
                    {t('Add Source')}
                  </Button>
                )
              }
            </Permissioned>
          </Space>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            rowSelection={null}
            size="small"
            rowKey="name"
            columns={columns}
            dataSource={knowledgeBases?.items}
            loading={isFetchingKnowledgeBasesWithoutPolling}
            onChange={handleTableChange}
            pagination={{
              total: _knowledgeBases?.total || 0,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: _knowledgeBases?.pageSize || 0,
              showSizeChanger: false,
              size: 'default'
            }}
          />
        </Col>
      </Row>

      <DeleteConfirmationModal
        title={t('Stop the upload process?')}
        content={renderCancelUploadModalContent()}
        onDelete={onCancelUpload}
        isDeleting={isCancelingUpload}
        isModalOpen={isCancelUploadModalOpen}
        closeModal={closeCancelUploadModal}
        deleteKeyword="Delete"
      />

      <KnowledgeBaseArticlesModal isModalOpen={isConnectKBModalOpen} closeModal={onConnectKBModalClose} selectedKnowledgeBase={knowledgeBaseToEdit} />
    </Fragment>
  );
};

export default HRWizKnowledgeBaseSourcesTable;
